<script>
import StakeholderCategoryService from "@/services/StakeholderCategoryService";

export default {
    props: {
        category: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            saving: false,
            entity: {
                name_col_active: "0",
                title_col_active: "0",
                address_col_active: "0",
                phone_col_active: "0",
                email_col_active: "0",
                note_col_active: "0",
                custom_string_col_active: "0",
                custom_string_col_name: null,
                custom_number_col_active: "0",
                custom_number_col_name: null
            },
            columns: []
        };
    },
    created() {
        // Load static column settings
        this.entity.name_col_active = this.category.name_col_active ? "1" : "0";
        this.entity.title_col_active = this.category.title_col_active
            ? "1"
            : "0";
        this.entity.address_col_active = this.category.address_col_active
            ? "1"
            : "0";
        this.entity.phone_col_active = this.category.phone_col_active
            ? "1"
            : "0";
        this.entity.email_col_active = this.category.email_col_active
            ? "1"
            : "0";
        this.entity.note_col_active = this.category.note_col_active ? "1" : "0";
        this.entity.custom_string_col_active = this.category
            .custom_string_col_active
            ? "1"
            : "0";
        this.entity.custom_string_col_name = this.category.custom_string_col_name;
        this.entity.custom_number_col_active = this.category
            .custom_number_col_active
            ? "1"
            : "0";
        this.entity.custom_number_col_name = this.category.custom_number_col_name;

        // Load custom columns
        this.columns = (this.category.custom_columns || []).map(col => ({
            id: col.id,
            name: col.name,
            sort_order: col.sort_order
        }));
    },
    methods: {
        addColumn() {
            this.columns.push({
                id: null,
                name: "",
                sort_order: this.columns.length + 1
            });
        },
        removeColumn(index) {
            this.columns.splice(index, 1);
            this.recalculateSortOrder();
        },
        moveUp(index) {
            if (index === 0) return;
            const temp = this.columns[index];
            this.$set(this.columns, index, this.columns[index - 1]);
            this.$set(this.columns, index - 1, temp);
            this.recalculateSortOrder();
        },
        moveDown(index) {
            if (index === this.columns.length - 1) return;
            const temp = this.columns[index];
            this.$set(this.columns, index, this.columns[index + 1]);
            this.$set(this.columns, index + 1, temp);
            this.recalculateSortOrder();
        },
        recalculateSortOrder() {
            this.columns.forEach((col, i) => {
                col.sort_order = i + 1;
            });
        },
        async save() {
            try {
                this.saving = true;
                const payload = {
                    id: this.category.id,
                    ...this.entity,
                    custom_columns: this.columns
                        .filter(c => c.name && c.name.trim())
                        .map((c, i) => ({
                            id: c.id,
                            name: c.name.trim(),
                            sort_order: i + 1
                        }))
                };
                await StakeholderCategoryService.PUT(payload);
                this.$emit("close-reload");
            } catch (e) {
                console.error("Error saving columns:", e);
            }
            this.saving = false;
        }
    }
};
</script>

<template>
    <modal :show="true" @close="$emit('close')" :loading="loading" size="lg">
        <template slot="header">
            {{ $t("stakeholders.manage_columns") }}
        </template>

        <template slot="default">
            <div class="row">
                <!-- Left side: Static columns -->
                <div class="col-6">
                    <h4 class="mb-3">
                        {{ $t("stakeholders.standard_columns") }}
                    </h4>

                    <div class="static-columns">
                        <label class="d-block mb-2">
                            <input
                                v-model="entity.name_col_active"
                                class="mr-2"
                                type="checkbox"
                                true-value="1"
                                false-value="0"
                            />
                            {{ $t("stakeholders.hdr_name") }}
                        </label>
                        <label class="d-block mb-2">
                            <input
                                v-model="entity.title_col_active"
                                class="mr-2"
                                type="checkbox"
                                true-value="1"
                                false-value="0"
                            />
                            {{ $t("stakeholders.hdr_title") }}
                        </label>
                        <label class="d-block mb-2">
                            <input
                                v-model="entity.address_col_active"
                                class="mr-2"
                                type="checkbox"
                                true-value="1"
                                false-value="0"
                            />
                            {{ $t("stakeholders.hdr_address") }}
                        </label>
                        <label class="d-block mb-2">
                            <input
                                v-model="entity.phone_col_active"
                                class="mr-2"
                                type="checkbox"
                                true-value="1"
                                false-value="0"
                            />
                            {{ $t("stakeholders.hdr_phone") }}
                        </label>
                        <label class="d-block mb-2">
                            <input
                                v-model="entity.email_col_active"
                                class="mr-2"
                                type="checkbox"
                                true-value="1"
                                false-value="0"
                            />
                            {{ $t("stakeholders.hdr_email") }}
                        </label>
                        <label class="d-block mb-2">
                            <input
                                v-model="entity.note_col_active"
                                class="mr-2"
                                type="checkbox"
                                true-value="1"
                                false-value="0"
                            />
                            {{ $t("stakeholders.hdr_note") }}
                        </label>

                        <hr class="my-3" />

                        <label class="d-block mb-2">
                            <input
                                v-model="entity.custom_string_col_active"
                                class="mr-2"
                                type="checkbox"
                                true-value="1"
                                false-value="0"
                            />
                            {{ $t("stakeholders.hdr_custom_string") }}
                            <input
                                v-model="entity.custom_string_col_name"
                                class="form-control form-control-sm mt-1"
                                type="text"
                                :placeholder="$t('stakeholders.type_title')"
                                :disabled="
                                    entity.custom_string_col_active !== '1'
                                "
                            />
                        </label>

                        <label class="d-block mb-2">
                            <input
                                v-model="entity.custom_number_col_active"
                                class="mr-2"
                                type="checkbox"
                                true-value="1"
                                false-value="0"
                            />
                            {{ $t("stakeholders.hdr_custom_number") }}
                            <input
                                v-model="entity.custom_number_col_name"
                                class="form-control form-control-sm mt-1"
                                type="text"
                                :placeholder="$t('stakeholders.type_title')"
                                :disabled="
                                    entity.custom_number_col_active !== '1'
                                "
                            />
                        </label>
                    </div>
                </div>

                <!-- Right side: Custom columns -->
                <div class="col-6">
                    <h4 class="mb-3">
                        {{ $t("stakeholders.custom_columns") }}
                    </h4>

                    <div class="columns-list">
                        <div
                            v-for="(col, index) in columns"
                            :key="index"
                            class="column-row d-flex align-items-center mb-2"
                        >
                            <div class="column-order mr-2">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-link p-0"
                                    :disabled="index === 0"
                                    @click="moveUp(index)"
                                >
                                    <i class="far fa-chevron-up"></i>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-link p-0"
                                    :disabled="index === columns.length - 1"
                                    @click="moveDown(index)"
                                >
                                    <i class="far fa-chevron-down"></i>
                                </button>
                            </div>
                            <input
                                v-model="col.name"
                                type="text"
                                class="form-control form-control-sm flex-grow-1"
                                :placeholder="$t('stakeholders.column_name')"
                            />
                            <base-button
                                type="danger"
                                size="sm"
                                outline
                                class="ml-2"
                                @click="removeColumn(index)"
                            >
                                <i class="far fa-trash"></i>
                            </base-button>
                        </div>

                        <div
                            v-if="columns.length === 0"
                            class="text-muted mb-3"
                        >
                            {{ $t("stakeholders.no_custom_columns") }}
                        </div>

                        <base-button
                            type="secondary"
                            size="sm"
                            outline
                            @click="addColumn"
                        >
                            <i class="far fa-plus"></i>
                            {{ $t("stakeholders.add_column") }}
                        </base-button>
                    </div>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col text-right">
                    <base-button
                        type="secondary"
                        :outline="true"
                        @click="$emit('close')"
                        >{{ $t("cancel") }}</base-button
                    >
                    <base-button
                        type="primary"
                        :loading="saving"
                        @click="save"
                        >{{ $t("save") }}</base-button
                    >
                </div>
            </div>
        </template>
    </modal>
</template>

<style scoped>
.column-order {
    display: flex;
    flex-direction: column;
    line-height: 1;
}
.column-order button {
    line-height: 1;
    color: #525f7f;
}
.column-order button:disabled {
    opacity: 0.3;
}
.static-columns label {
    cursor: pointer;
}
</style>
